import { useEffect, useRef, useSyncExternalStore } from 'react';
import {
  registerShortcut, unregisterShortcut, matchesCombo,
  getRegisteredShortcuts, subscribeToShortcuts,
} from '@/lib/globalShortcuts';

/**
 * Component-side hook to register a global keyboard shortcut for the lifetime
 * of the calling component. The handler is read through a ref so the registered
 * shortcut always sees the latest closure (no stale state).
 *
 * Usage:
 *   useGlobalShortcut({
 *     id: 'report-builder.save',
 *     keys: 'mod+s',
 *     label: 'Save report',
 *     category: 'Editing',
 *     handler: () => saveReport(),
 *   });
 *
 * Pass `enabled: false` to temporarily disable without unmounting.
 */
export function useGlobalShortcut({
  id, keys, label, category, handler,
  preventDefault = true, allowInInputs = false, enabled = true,
}) {
  const handlerRef = useRef(handler);
  useEffect(() => { handlerRef.current = handler; });

  useEffect(() => {
    if (!enabled) return undefined;
    registerShortcut({
      id, keys, label, category,
      preventDefault, allowInInputs,
      // Always read the latest handler closure
      handler: (e) => handlerRef.current?.(e),
    });
    return () => unregisterShortcut(id);
  }, [id, label, category, preventDefault, allowInInputs, enabled, keysSignature(keys)]);
}

function keysSignature(keys) {
  return Array.isArray(keys) ? keys.join('|') : String(keys || '');
}

/**
 * Subscribes to the registry so help-dialog UIs re-render when shortcuts are
 * added/removed.
 */
export function useRegisteredShortcuts() {
  return useSyncExternalStore(
    subscribeToShortcuts,
    getRegisteredShortcuts,
    getRegisteredShortcuts, // server snapshot — fine since registry is empty on SSR
  );
}

/**
 * Mounts a single global keydown listener that dispatches matched events to
 * the registered handler. Drop one <GlobalShortcutsProvider> at the App level.
 */
export function GlobalShortcutsProvider({ children }) {
  useEffect(() => {
    const onKeyDown = (e) => {
      const target = e.target;
      const inEditable = isEditableTarget(target);
      // Iterate snapshot; later registrations win on collision (rare since `id` is unique).
      const shortcuts = getRegisteredShortcuts();
      for (const sc of shortcuts) {
        if (inEditable && !sc.allowInInputs) continue;
        if (!matchesCombo(e, sc.keys)) continue;
        if (sc.preventDefault) e.preventDefault();
        try { sc.handler(e); } catch (err) { console.error(`shortcut "${sc.id}" failed:`, err); }
        return; // first match wins
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return children;
}

function isEditableTarget(t) {
  if (!t) return false;
  const tag = (t.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (t.isContentEditable) return true;
  return false;
}
